import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  UpdateProjectRequestBody,
  UpdateProjectResponseBody,
} from "@imagine/ai-service/shared-types";
import { toast } from "sonner";
import { imagineStore } from "@/stores/imagine.store";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";

export function useRenameProject({
  onSuccess,
  onError,
}: { onSuccess?: () => void; onError?: (error: Error) => void } = {}) {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateProjectResponseBody,
    Error,
    { projectId: string; name: string }
  >({
    mutationFn: async ({ projectId, name }) => {
      const body: UpdateProjectRequestBody = { name };

      const response = await aiServiceFetch(`/api/projects/${projectId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw await HttpError.fromResponse(response);
      }

      return response.json();
    },
    onError: (error) => {
      onError?.(error);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["my-projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", data.id] });

      // Update the project in the store if it's the current project
      const currentProject = imagineStore.getState().project;
      if (currentProject && currentProject.id === data.id) {
        imagineStore.getState().setProject({
          ...currentProject,
          name: data.name,
          updatedAt: new Date(data.updatedAt),
        });
      }

      toast.success("Project renamed successfully");
      onSuccess?.();
    },
  });
}
